import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { cn } from 'ui/lib/utils';

/* ─────────────────────────────────────────── */
/* Types                                       */
/* ─────────────────────────────────────────── */

type ToastVariant = 'success' | 'error' | 'warning' | 'info';

interface ToastData {
	id: string;
	title: string;
	description?: string;
	variant: ToastVariant;
}

interface ToastInput {
	title: string;
	description?: string;
	variant?: ToastVariant;
}

interface ToastContextValue {
	toast: (input: ToastInput) => void;
}

/* ─────────────────────────────────────────── */
/* Variant config                              */
/* ─────────────────────────────────────────── */

// Local: el design system deja los avisos de error y de advertencia abiertos hasta que se cierran a mano; acá se
// cierran solos a los 8 s para que no se acumulen sobre el contenido (success e info siguen en 4 s).
const variantConfig: Record<ToastVariant, { bg: string; icon: string; duration: number }> = {
	success: { bg: '#1E7E34', icon: '\u2713', duration: 4000 },
	error: { bg: '#D93025', icon: '\u2717', duration: 8000 },
	warning: { bg: '#E65100', icon: '\u26A0', duration: 8000 },
	info: { bg: '#1A3762', icon: '\u2139', duration: 4000 }
};

/* ─────────────────────────────────────────── */
/* Context                                     */
/* ─────────────────────────────────────────── */

const ToastContext = createContext<ToastContextValue | null>(null);

/* ─────────────────────────────────────────── */
/* Single Toast Item                           */
/* ─────────────────────────────────────────── */

function ToastItem({ data, onDismiss }: { data: ToastData; onDismiss: (id: string) => void }) {
	const config = variantConfig[data.variant];
	const [visible, setVisible] = useState(false);
	const [exiting, setExiting] = useState(false);
	const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	useEffect(() => {
		// Trigger entrance animation on next frame
		const raf = requestAnimationFrame(() => setVisible(true));
		return () => cancelAnimationFrame(raf);
	}, []);

	useEffect(() => {
		timerRef.current = setTimeout(() => {
			handleClose();
		}, config.duration);
		return () => {
			if (timerRef.current) clearTimeout(timerRef.current);
		};
	}, []);

	const handleClose = useCallback(() => {
		setExiting(true);
		setTimeout(() => onDismiss(data.id), 200);
	}, [onDismiss, data.id]);

	return (
		<div
			role="alert"
			data-testid={`toast-${data.variant}`}
			className={cn(
				'pointer-events-auto flex w-80 items-start gap-3 rounded-[var(--safi-radius-md)] p-4 text-white shadow-lg transition-all',
				visible && !exiting ? 'translate-x-0 opacity-100' : 'translate-x-4 opacity-0'
			)}
			style={{
				backgroundColor: config.bg,
				transitionDuration: 'var(--duration-fast)'
			}}>
			<span
				className="mt-0.5 flex-shrink-0 text-lg"
				aria-hidden="true">
				{config.icon}
			</span>
			<div className="min-w-0 flex-1">
				<p className="text-sm font-semibold leading-5">{data.title}</p>
				{data.description && <p className="mt-0.5 text-sm leading-5 opacity-90">{data.description}</p>}
			</div>
			<button
				type="button"
				onClick={handleClose}
				aria-label="Cerrar notificación"
				className="flex-shrink-0 cursor-pointer rounded p-0.5 text-white opacity-70 transition-opacity hover:opacity-100">
				<svg
					width="14"
					height="14"
					viewBox="0 0 14 14"
					fill="none"
					xmlns="http://www.w3.org/2000/svg">
					<path
						d="M11 3L3 11M3 3l8 8"
						stroke="currentColor"
						strokeWidth="1.5"
						strokeLinecap="round"
					/>
				</svg>
			</button>
		</div>
	);
}

/* ─────────────────────────────────────────── */
/* Provider                                    */
/* ─────────────────────────────────────────── */

let toastCounter = 0;

export function ToastProvider({ children }: { children: ReactNode }) {
	const [toasts, setToasts] = useState<ToastData[]>([]);

	const toast = useCallback((input: ToastInput) => {
		const id = `toast-${++toastCounter}-${Date.now()}`;
		const newToast: ToastData = {
			id,
			title: input.title,
			description: input.description,
			variant: input.variant ?? 'info'
		};
		setToasts((prev) => [...prev, newToast]);
	}, []);

	const dismiss = useCallback((id: string) => {
		setToasts((prev) => prev.filter((t) => t.id !== id));
	}, []);

	return (
		<ToastContext.Provider value={{ toast }}>
			{children}
			{/* Toast container — bottom-right, stacked */}
			<div
				data-testid="toast-container"
				aria-live="polite"
				aria-atomic="false"
				className="pointer-events-none fixed right-4 bottom-4 z-[9999] flex flex-col-reverse gap-2">
				{toasts.map((t) => (
					<ToastItem
						key={t.id}
						data={t}
						onDismiss={dismiss}
					/>
				))}
			</div>
		</ToastContext.Provider>
	);
}

/* ─────────────────────────────────────────── */
/* Hook                                        */
/* ─────────────────────────────────────────── */

export function useToast(): ToastContextValue {
	const ctx = useContext(ToastContext);
	if (!ctx) {
		throw new Error('useToast must be used within a <ToastProvider>');
	}
	return ctx;
}

export default ToastProvider;
